import * as React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  MenuItem,
  Button,
  Alert,
  Divider,
  InputAdornment,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  useMediaQuery,
} from "@mui/material";

import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import BedOutlinedIcon from "@mui/icons-material/BedOutlined";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ArrowRightAltOutlinedIcon from "@mui/icons-material/ArrowRightAltOutlined";

import { listHospitals } from "../api/hospitalsApi";
import { listDepartments } from "../api/locationsApi";
import { searchPatientsByPhone } from "../api/patientsApi";
import { createAdmission } from "../api/admissionsApi";
import { prettyError } from "../utils/prettyError";

import { Colors } from "../../utils/Colors";

const ADMIT_SOURCE_OPTIONS = [
  { value: "ER", label: "ER" },
  { value: "OPD", label: "OPD" },
  { value: "TRANSFER", label: "Transfer" },
  { value: "OTHER", label: "Other" },
];

const STATUS_OPTIONS = [
  { value: "ACTIVE", label: "Active" },
  { value: "DISCHARGED", label: "Discharged" },
  { value: "CANCELLED", label: "Cancelled" },
];

// ---------------- helpers ----------------
function fkId(value) {
  if (!value) return null;
  if (typeof value === "object") return value.id ?? value.uuid ?? value.pk ?? null;
  return value;
}

function getId(obj) {
  return (
    obj?.id ??
    obj?.uuid ??
    obj?.pk ??
    obj?.hospital_id ??
    obj?.organization_id ??
    obj?.department_id ??
    null
  );
}

function getLabel(obj) {
  return (
    obj?.name ??
    obj?.title ??
    obj?.department_name ??
    obj?.code ??
    null
  );
}

function getParentHospitalId(obj) {
  const v =
    obj?.hospital ??
    obj?.organization ??
    obj?.hospital_id ??
    obj?.organization_id ??
    obj?.org ??
    obj?.org_id ??
    obj?.hospital_uuid ??
    obj?.organization_uuid ??
    null;
  return fkId(v);
}

function toIsoFromLocal(localValue) {
  const d = new Date(localValue);
  return Number.isNaN(d.getTime()) ? localValue : d.toISOString();
}

function normalizeOptions(list, labelFn = getLabel) {
  return (list || [])
    .map((x) => {
      const id = getId(x);
      const label = labelFn(x);
      if (id === null || id === undefined) return null;
      if (!label) return null;
      return { id: String(id), label, raw: x };
    })
    .filter(Boolean);
}

function renderSelectValue(placeholder, options) {
  return (val) => {
    if (!val) return <span style={{ color: Colors.text.placeholder }}>{placeholder}</span>;
    const found = options?.find((o) => String(o.id) === String(val));
    return found?.label ?? String(val);
  };
}

// ---------- layout ----------
const LABEL_COL_PX = 190;

function FormRow({ label, children }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: `${LABEL_COL_PX}px 1fr` },
        columnGap: { sm: 2 },
        rowGap: { xs: 0.5, sm: 1 },
        alignItems: "center",
        py: { xs: 0.25, sm: 0.55 },
      }}
    >
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: { xs: 12, sm: 13 },
          textAlign: { xs: "left", sm: "right" },
          color: Colors.text.header,
        }}
      >
        {label}
      </Typography>
      <Box sx={{ width: "100%", minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ fontWeight: 900, fontSize: 13, color: Colors.text.title, mb: 1 }}>
        {title}
      </Typography>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          p: { xs: 1, sm: 1.5, md: 2 },
          background: Colors.bg.paper,
          borderColor: Colors.border.light,
          overflow: "hidden",
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}

export default function AdmissionCreateSmartForm() {
  const isXsTight = useMediaQuery("(max-width:400px)");

  // master lists
  const [hospitals, setHospitals] = React.useState([]);
  const [departmentsAll, setDepartmentsAll] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);

  // patient search
  const [phone, setPhone] = React.useState("");
  const [patientResults, setPatientResults] = React.useState([]);
  const [selectedPatient, setSelectedPatient] = React.useState(null);

  const emptyForm = React.useMemo(
    () => ({
      hospital: "",
      department: "",
      ward: "",
      room: "",
      bed: "",
      admit_at: "",
      discharge_at: "",
      admit_source: "",
      status: "ACTIVE",
      treating_doctor: "",
      consultant: "",
    }),
    []
  );

  const [form, setForm] = React.useState(emptyForm);

  const [loadingInit, setLoadingInit] = React.useState(false);
  const [loadingSearch, setLoadingSearch] = React.useState(false);
  const [loadingSave, setLoadingSave] = React.useState(false);

  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");

  const onChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const INPUT_H = isXsTight ? 38 : 44;
  const ICON_W = isXsTight ? 30 : 44;

  const fieldSx = React.useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        borderRadius: 2,
        background: Colors.bg.paper,
        height: INPUT_H,
        overflow: "hidden",
      },
      "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.border.input },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: Colors.text.sub,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: Colors.brand.primary,
        borderWidth: "2px",
      },
      "& .MuiOutlinedInput-input": {
        paddingTop: 0,
        paddingBottom: 0,
        height: "100%",
        display: "flex",
        alignItems: "center",
        color: Colors.text.body,
        fontSize: isXsTight ? 12.5 : 14,
        minWidth: 0,
      },
      "& input::placeholder": { color: Colors.text.placeholder, opacity: 1 },
    }),
    [INPUT_H, isXsTight]
  );

  const iconBoxSx = React.useMemo(
    () => ({
      width: ICON_W,
      height: INPUT_H,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: Colors.bg.hover,
      borderRight: `1px solid ${Colors.border.light}`,
      borderRadius: "8px 0 0 8px",
      mr: isXsTight ? 0.4 : 1,
      color: Colors.text.icon,
      flex: "0 0 auto",
    }),
    [ICON_W, INPUT_H, isXsTight]
  );

  // 1) load once
  React.useEffect(() => {
    (async () => {
      setLoadingInit(true);
      setError("");
      try {
        const [h, d] = await Promise.all([
          listHospitals(),
          listDepartments(),
        ]);

        setHospitals(h);
        setDepartmentsAll(d);
      } catch (e) {
        setError(prettyError(e));
      } finally {
        setLoadingInit(false);
      }
    })();
  }, []);

  // 2) hospital -> departments only
  React.useEffect(() => {
    const hospitalId = form.hospital;

    if (!hospitalId) {
      setDepartments([]);
      return;
    }

    setDepartments(
      departmentsAll.filter((x) => String(getParentHospitalId(x)) === String(hospitalId))
    );

    setForm((p) => ({
      ...p,
      department: "",
    }));
  }, [form.hospital, departmentsAll]);

  async function handleSearchPatient() {
    setError("");
    setSuccess("");
    setSelectedPatient(null);
    setPatientResults([]);

    const q = String(phone || "").trim();
    if (!q) return setError("Enter patient phone number.");

    setLoadingSearch(true);
    try {
      const results = await searchPatientsByPhone(q);
      setPatientResults(results);
      if (!results.length) setError("No patient found for this phone.");
    } catch (e) {
      setError(prettyError(e));
    } finally {
      setLoadingSearch(false);
    }
  }

  function resetAll() {
    setPhone("");
    setPatientResults([]);
    setSelectedPatient(null);
    setDepartments([]);
    setForm(emptyForm);
  }

  async function handleCreateAdmission() {
    setError("");
    setSuccess("");

    if (!selectedPatient?.uhid) return setError("Select a patient first.");
    if (!form.hospital) return setError("Select Hospital.");
    if (!form.department) return setError("Select Department.");
    if (!form.admit_at) return setError("Admit date/time is required.");
    if (!form.admit_source) return setError("Select admit source.");

    setLoadingSave(true);
    try {
      const payload = {
        patient_uhid: String(selectedPatient.uhid),
        organization: form.hospital,
        department: form.department,
        ward_unit: form.ward?.trim() || null,
        bed_number: form.bed?.trim() || null,
        admit_at: toIsoFromLocal(form.admit_at),
        discharge_at: form.discharge_at ? toIsoFromLocal(form.discharge_at) : null,
        admit_source: form.admit_source,
        status: form.status,
        treating_doctor: form.treating_doctor?.trim() || null,
        consultant: form.consultant?.trim() || null,
        room: form.room?.trim() || null,
      };

      const created = await createAdmission(payload);
      setSuccess(`Admission created ✅ Admission No: ${created?.admission_no ?? "auto"}`);
      resetAll();
    } catch (e) {
      setError(prettyError(e));
    } finally {
      setLoadingSave(false);
    }
  }

  const hospitalOptions = normalizeOptions(
    hospitals,
    (h) => h?.name ?? h?.organization_name ?? h?.title
  );
  const departmentOptions = normalizeOptions(departments);

  return (
    <Box sx={{ p: isXsTight ? 0.75 : 1.5, background: Colors.bg.app, minHeight: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          variant="outlined"
          sx={{
            width: "100%",
            maxWidth: 920,
            borderRadius: 3,
            boxShadow: Colors.shadow.brand,
            borderColor: Colors.border.light,
            background: Colors.bg.paper,
          }}
        >
          <CardContent sx={{ p: isXsTight ? 1.25 : { xs: 1.5, sm: 2, md: 3 } }}>
            <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.25, color: Colors.text.title }}>
              Admission Form
            </Typography>

            <Divider sx={{ my: 2, borderColor: Colors.border.topLine }} />

            {loadingInit ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Loading master data…
              </Alert>
            ) : null}

            {error ? (
              <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
                {error}
              </Alert>
            ) : null}

            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            ) : null}

            <Section title="Patient Search">
              <FormRow label="Phone #">
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth
                      size="small"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="Enter phone number"
                      sx={fieldSx}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start" sx={iconBoxSx}>
                            <LocalPhoneOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSearchPatient}
                      disabled={loadingSearch}
                      startIcon={<ArrowRightAltOutlinedIcon />}
                      sx={{
                        height: INPUT_H,
                        borderRadius: 2,
                        fontWeight: 900,
                        backgroundColor: Colors.brand.primary,
                        "&:hover": { backgroundColor: Colors.brand.primaryDark },
                      }}
                    >
                      {loadingSearch ? "Finding..." : "FIND"}
                    </Button>
                  </Grid>
                </Grid>
              </FormRow>

              {patientResults.length > 0 ? (
                <Box sx={{ mt: 1, ml: { xs: 0, sm: `${LABEL_COL_PX}px` } }}>
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      overflow: "hidden",
                      borderColor: Colors.border.light,
                    }}
                  >
                    <List dense sx={{ p: 0 }}>
                      {patientResults.map((p) => (
                        <ListItemButton
                          key={String(p.patient_id ?? p.id ?? p.uhid)}
                          onClick={() => setSelectedPatient(p)}
                        >
                          <ListItemText
                            primary={`${p.full_name ?? "Unknown"} (UHID: ${p.uhid ?? "-"})`}
                            secondary={`Phone: ${p.primary_phone ?? "-"}`}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                </Box>
              ) : null}

              <FormRow label="Selected Patient">
                <TextField
                  fullWidth
                  size="small"
                  value={
                    selectedPatient
                      ? `${selectedPatient.full_name ?? "—"} | UHID: ${selectedPatient.uhid ?? "—"}`
                      : ""
                  }
                  placeholder="------ Select patient from results ------"
                  sx={fieldSx}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start" sx={iconBoxSx}>
                        {selectedPatient ? (
                          <BadgeOutlinedIcon fontSize="small" />
                        ) : (
                          <PersonOutlineIcon fontSize="small" />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </FormRow>
            </Section>

            <Section title="Admission Details">
              <FormRow label="Hospital">
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={form.hospital}
                  onChange={onChange("hospital")}
                  sx={fieldSx}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: renderSelectValue("------ Select Hospital ------", hospitalOptions),
                  }}
                  InputProps={{
                    startAdornment: isXsTight ? null : (
                      <InputAdornment position="start" sx={iconBoxSx}>
                        <LocalHospitalOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  {hospitalOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormRow>

              <FormRow label="Department">
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={form.department}
                  onChange={onChange("department")}
                  disabled={!form.hospital}
                  sx={fieldSx}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: renderSelectValue("------ Department ------", departmentOptions),
                  }}
                  InputProps={{
                    startAdornment: isXsTight ? null : (
                      <InputAdornment position="start" sx={iconBoxSx}>
                        <ApartmentOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                >
                  {departmentOptions.map((o) => (
                    <MenuItem key={o.id} value={o.label}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormRow>

              <FormRow label="Ward / Unit">
                <TextField
                  fullWidth
                  size="small"
                  value={form.ward}
                  onChange={onChange("ward")}
                  placeholder="Enter ward / unit"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={iconBoxSx}>
                        <MeetingRoomOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormRow>

              <FormRow label="Room">
                <TextField
                  fullWidth
                  size="small"
                  value={form.room}
                  onChange={onChange("room")}
                  placeholder="Enter room"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={iconBoxSx}>
                        <MeetingRoomOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormRow>

              <FormRow label="Bed">
                <TextField
                  fullWidth
                  size="small"
                  value={form.bed}
                  onChange={onChange("bed")}
                  placeholder="Enter bed number"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={iconBoxSx}>
                        <BedOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormRow>

              <FormRow label="Admit Date/Time">
                <TextField
                  type="datetime-local"
                  fullWidth
                  size="small"
                  value={form.admit_at}
                  onChange={onChange("admit_at")}
                  sx={fieldSx}
                  InputLabelProps={{ shrink: true }}
                />
              </FormRow>

              <FormRow label="Admit Source">
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={form.admit_source}
                  onChange={onChange("admit_source")}
                  sx={fieldSx}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (v) =>
                      !v ? (
                        <span style={{ color: Colors.text.placeholder }}>
                          ------ Admit Source ------
                        </span>
                      ) : (
                        ADMIT_SOURCE_OPTIONS.find((o) => o.value === v)?.label ?? String(v)
                      ),
                  }}
                >
                  {ADMIT_SOURCE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormRow>

              <FormRow label="Status">
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={form.status}
                  onChange={onChange("status")}
                  sx={fieldSx}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </FormRow>

              <FormRow label="Treating Doctor">
                <TextField
                  fullWidth
                  size="small"
                  value={form.treating_doctor}
                  onChange={onChange("treating_doctor")}
                  placeholder="Enter treating doctor"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={iconBoxSx}>
                        <LocalHospitalIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormRow>

              <FormRow label="Consultant">
                <TextField
                  fullWidth
                  size="small"
                  value={form.consultant}
                  onChange={onChange("consultant")}
                  placeholder="Enter consultant"
                  sx={fieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={iconBoxSx}>
                        <LocalHospitalIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </FormRow>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mt: 2,
                  gap: 1,
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Button
                  variant="contained"
                  onClick={handleCreateAdmission}
                  disabled={loadingSave}
                  fullWidth
                  sx={{
                    height: INPUT_H,
                    borderRadius: 2,
                    fontWeight: 900,
                    backgroundColor: Colors.brand.primary,
                    "&:hover": { backgroundColor: Colors.brand.primaryDark },
                  }}
                >
                  {loadingSave ? "CREATING..." : "CREATE ADMISSION"}
                </Button>

                <Button
                  variant="outlined"
                  onClick={resetAll}
                  fullWidth
                  sx={{
                    height: INPUT_H,
                    borderRadius: 2,
                    fontWeight: 900,
                    color: Colors.brand.primary,
                    borderColor: Colors.border.input,
                    "&:hover": { borderColor: Colors.brand.primary, background: Colors.bg.hover },
                  }}
                >
                  RESET
                </Button>
              </Box>
            </Section>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}